import { useEffect, useState } from 'react'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { Alert, ButtonBase, CircularProgress, Grid, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import Api from '../../utils/api'
import { formatAddress } from '../../utils/helpers'
import FullScreenModal from '../full-screen-modal'

import useStyles from './index.styles'

interface IProps {
  open: boolean
  onClose: () => void
  folderIds: number[]
  onSelect: (folderId: number) => void
  selectedFolderId: number | null | undefined
}

const LocationSelectModal = (props: IProps) => {
  const { open, folderIds, onClose, onSelect, selectedFolderId } = props

  const classes = useStyles
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const [folders, setFolders] = useState<IFolderType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const isCurrentlyViewingFolder = (folderId: number) => folderId === selectedFolderId

  useEffect(() => {
    const getFolders = async () => {
      if (folderIds.length > 0) {
        setLoading(true)
        Api.post('/api/folders/query', {
          filters: [
            {
              fieldName: 'id',
              operator: 'in',
              value: folderIds,
            },
            {
              fieldName: 'type',
              operator: 'eq',
              value: 'Participant',
            },
          ],
        }).then((res) => {
          setLoading(false)
          if (res.ok) {
            const sortedFolders = res.body.results.sort((a: IFolderType, b: IFolderType) => {
              // Put the currently selected folder at the top
              if (a.id === selectedFolderId) return -1
              if (b.id === selectedFolderId) return 1
              return 0
            })
            setFolders(sortedFolders)
          } else {
            setError('Could not load locations. Please try again later')
          }
        })
      }
    }

    getFolders()
  }, [folderIds, selectedFolderId])

  const handleLocationSelect = (selectedFolder: IFolderType) => {
    onSelect(selectedFolder.id)
    onClose()
    if (selectedFolderId) {
      enqueueSnackbar(`Viewing: ${selectedFolder.name}`)
    }
  }

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      disableBackdropClick={!selectedFolderId}
      title="Select Location"
      showCloseIcon={!!selectedFolderId}
      maxWidth="sm"
    >
      <Grid container={true} spacing={0}>
        {loading && !error && <CircularProgress sx={classes.spinner} data-testid="spinner" />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <>
            {folders.map((folder) => (
              <ButtonBase
                key={folder.id}
                sx={{
                  ...classes.locationWrapper(theme),
                  ...(isCurrentlyViewingFolder(folder.id) ? classes.currentFolder : {}),
                }}
                onClick={() => handleLocationSelect(folder)}
              >
                <Grid size={12} container={true} spacing={0} sx={classes.folderContainer}>
                  <Grid size={12}>
                    {isCurrentlyViewingFolder(folder.id) && (
                      <Typography variant="body2" sx={classes.currentlyViewingText}>
                        Currently viewing
                      </Typography>
                    )}
                  </Grid>

                  <Grid size={12} container={true} spacing={1} sx={classes.folderContent}>
                    <Grid size="auto" sx={classes.locationIcon}>
                      <LocationOnOutlinedIcon sx={classes.locationIcon(theme)} fontSize="small" />
                    </Grid>

                    <Grid size="grow">
                      <Typography variant="body1" className="fs-exclude" noWrap={true}>
                        {folder.name}
                      </Typography>

                      <Typography variant="body2" color="textSecondary" noWrap={true} className="fs-exclude" sx={classes.locationAddress}>
                        {formatAddress(folder.information.address)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </ButtonBase>
            ))}
          </>
        )}
      </Grid>
    </FullScreenModal>
  )
}

export default LocationSelectModal
